import { html, nothing, unsafeCSS, type CSSResultGroup, type PropertyValues } from 'lit';
import { live } from 'lit/directives/live.js';
import { customElement, property, state } from 'lit/decorators.js';
import { LibElement } from '../lib-element';
import css from './lib-time-picker.css';

type Segment = 'hour' | 'minute';
interface Feedback { text: string; error: boolean; external: boolean; }

const last: Record<Segment, number> = { hour: 23, minute: 59 };
const pad = (value: number): string => String(value).padStart(2, '0');
const parseValue = (value: string): [number, number] | null => {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const hour = Number(match[1]), minute = Number(match[2]);
  return hour <= 23 && minute <= 59 ? [hour, minute] : null;
};

/**
 * An inline hour/minute selection panel (24-hour, minute precision).
 *
 * `value` is `HH:mm` or empty. `min-minute`/`max-minute` are inclusive bounds as minutes of the
 * day; a bound outside 0-1439 simply disables the affected choices. A `change` event is
 * dispatched for a complete, in-range value or a cleared selection that differs from `value`.
 * Invalid drafts stay local; `lib-time-picker-input` reports their localized validation error.
 */
@customElement('lib-time-picker')
export class LibTimePicker extends LibElement {
  protected override get hostFocusSelector(): string { return 'input, [role="option"]'; }
  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(css)];
  private revision = 0;
  static override get observedAttributes(): string[] { return [...super.observedAttributes, 'server-state']; }
  override attributeChangedCallback(name: string, previous: string | null, value: string | null): void {
    if (name === 'server-state') {
      if (value !== null) {
        const state = JSON.parse(value) as Record<string, string> & { revision: number };
        if (state.revision >= this.revision) {
          this.value = state.value;
        }
      }
      return;
    }
    if (['value'].includes(name) && this.hasAttribute('server-state')) return;
    super.attributeChangedCallback(name, previous, value);
  }
  protected override createRenderRoot(): HTMLElement | DocumentFragment {
    const root = super.createRenderRoot();
    // Capture before nested controls stop propagation; rendering here could replace native input before its handler reads it.
    root.addEventListener('input', () => { this.revision++; }, true);
    return root;
  }
  @property() value = '';
  @property({ type: Number, attribute: 'min-minute' }) minMinute: number | null = null;
  @property({ type: Number, attribute: 'max-minute' }) maxMinute: number | null = null;
  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean }) error = false;
  @property({ type: Boolean }) card = false;
  @property() message = '';
  @property({ attribute: 'group-label' }) groupLabel = 'Time';
  @property({ attribute: 'hours-label' }) hoursLabel = 'Hours';
  @property({ attribute: 'minutes-label' }) minutesLabel = 'Minutes';
  @property({ attribute: 'invalid-hour-message' }) invalidHourMessage = 'Enter an hour from 00 to 23.';
  @property({ attribute: 'invalid-minute-message' }) invalidMinuteMessage = 'Enter minutes from 00 to 59.';
  @property({ attribute: 'select-hour-message' }) selectHourMessage = 'Select an hour.';
  @property({ attribute: 'select-minute-message' }) selectMinuteMessage = 'Select minutes.';
  @property({ attribute: 'range-message' }) rangeMessage = 'Choose a time within the allowed range.';
  @property({ attribute: 'unfinished-message' }) unfinishedMessage = 'Finish entering the time.';
  @state() private hour: number | null = null;
  @state() private minute: number | null = null;
  @state() private hourText = '';
  @state() private minuteText = '';
  @state() private hourInvalid = false;
  @state() private minuteInvalid = false;
  @state() private hourFocus: number | null = null;
  @state() private minuteFocus: number | null = null;
  private published: string | null = null;
  private readonly pendingScroll = new Set<Segment>(['hour', 'minute']);
  private pendingFocus: Segment | null = null;
  private reportedRevision = -1;
  private reportedError = '';
  private resizeObserver?: ResizeObserver;

  private get lower(): number { return this.minMinute ?? 0; }
  private get upper(): number { return this.maxMinute ?? 1439; }
  private inRange(minuteOfDay: number): boolean { return minuteOfDay >= this.lower && minuteOfDay <= this.upper; }
  private get candidate(): number | null {
    return this.hour !== null && this.minute !== null ? this.hour * 60 + this.minute : null;
  }
  private get draftValue(): string {
    return this.hour !== null && this.minute !== null ? `${pad(this.hour)}:${pad(this.minute)}` : '';
  }

  /**
   * An hour is selectable when any of its minutes is in range. A minute is checked against the
   * selected hour, or against every hour while no hour is selected.
   */
  private selectable(segment: Segment, value: number): boolean {
    if (this.disabled) return false;
    if (segment === 'hour') return Math.max(value * 60, this.lower) <= Math.min(value * 60 + 59, this.upper);
    if (this.hour !== null && !this.hourInvalid) return this.inRange(this.hour * 60 + value);
    for (let hour = 0; hour <= 23; hour++) if (this.inRange(hour * 60 + value)) return true;
    return false;
  }

  private get rangeError(): boolean {
    if (this.hourInvalid || this.minuteInvalid) return false;
    const candidate = this.candidate;
    if (candidate !== null) return !this.inRange(candidate);
    if (this.hour !== null) return !this.selectable('hour', this.hour);
    if (this.minute !== null) return !this.selectable('minute', this.minute);
    return false;
  }

  private get inputError(): string {
    const hour = this.hourText.trim(), minute = this.minuteText.trim();
    if (hour && (!/^\d{1,2}$/.test(hour) || Number(hour) > 23)) return this.invalidHourMessage;
    if (minute && (!/^\d{1,2}$/.test(minute) || Number(minute) > 59)) return this.invalidMinuteMessage;
    if (!hour && !minute) return '';
    if (!hour) return this.selectHourMessage;
    if (!minute) return this.selectMinuteMessage;
    if (!this.inRange(Number(hour) * 60 + Number(minute))) return this.rangeMessage;
    return `${pad(Number(hour))}:${pad(Number(minute))}` !== this.value ? this.unfinishedMessage : '';
  }

  private reportInput(): void {
    const error = this.inputError;
    if (error === this.reportedError && this.reportedRevision === this.revision) return;
    this.reportedRevision = this.revision;
    this.reportedError = error;
    this.emit('lib-time-picker-input', { error, revision: this.revision });
  }

  private get feedback(): Feedback | null {
    const internal = (text: string, error = true): Feedback => ({ text, error, external: false });
    if (this.hourInvalid) return internal(this.invalidHourMessage);
    if (this.minuteInvalid) return internal(this.invalidMinuteMessage);
    if (this.rangeError) return internal(this.rangeMessage);
    if (this.hour !== null && this.minute === null) return internal(this.selectMinuteMessage, false);
    if (this.hour === null && this.minute !== null) return internal(this.selectHourMessage, false);
    return this.message ? { text: this.message, error: true, external: true } : null;
  }

  protected override willUpdate(changed: PropertyValues<this>): void {
    if (changed.has('value')) {
      if (this.value !== this.published) this.reset();
      this.published = null;
    }
  }

  private reset(): void {
    const parsed = parseValue(this.value);
    this.hour = parsed?.[0] ?? null;
    this.minute = parsed?.[1] ?? null;
    this.hourText = this.hour === null ? '' : pad(this.hour);
    this.minuteText = this.minute === null ? '' : pad(this.minute);
    this.hourInvalid = this.minuteInvalid = false;
    this.hourFocus = this.minuteFocus = null;
    this.pendingScroll.add('hour').add('minute');
  }

  override connectedCallback(): void {
    super.connectedCallback();
    if (this.hasUpdated) this.observeLists();
  }

  override disconnectedCallback(): void {
    this.resizeObserver?.disconnect();
    super.disconnectedCallback();
  }

  protected override firstUpdated(): void { this.observeLists(); }

  private observeLists(): void {
    const observer = this.resizeObserver ??= new ResizeObserver(() => this.applyScroll());
    observer.disconnect();
    this.renderRoot.querySelectorAll('[role=listbox]').forEach(list => observer.observe(list));
  }

  protected override updated(): void {
    this.applyScroll();
    if (this.pendingFocus) {
      const segment = this.pendingFocus;
      this.pendingFocus = null;
      const option = this.option(segment, this.rovingIndex(segment));
      if (option) {
        option.focus({ preventScroll: true });
        this.keepVisible(option);
      }
    }
    this.reportInput();
  }

  private list(segment: Segment): HTMLElement | null {
    return this.renderRoot.querySelector<HTMLElement>(`#${segment}-list`);
  }

  private option(segment: Segment, value: number | null): HTMLElement | null {
    return value === null ? null : this.renderRoot.querySelector<HTMLElement>(`#${segment}-option-${value}`);
  }

  /** Centres the selected (or first selectable) choice inside its own list without scrolling ancestors. */
  private applyScroll(): void {
    for (const segment of this.pendingScroll) {
      const list = this.list(segment);
      if (!list || list.clientHeight === 0) continue;
      this.pendingScroll.delete(segment);
      const target = this.option(segment, (segment === 'hour' ? this.hour : this.minute) ?? this.find(segment, 0, 1));
      if (target) list.scrollTop = target.offsetTop - (list.clientHeight - target.offsetHeight) / 2;
    }
  }

  private keepVisible(option: HTMLElement): void {
    const list = option.parentElement;
    if (!list) return;
    if (option.offsetTop < list.scrollTop) list.scrollTop = option.offsetTop;
    else if (option.offsetTop + option.offsetHeight > list.scrollTop + list.clientHeight)
      list.scrollTop = option.offsetTop + option.offsetHeight - list.clientHeight;
  }

  /** First selectable value from `from` towards `step`, stopping at `stop` (inclusive) or the list end. */
  private find(segment: Segment, from: number, step: number, stop = step > 0 ? last[segment] : 0): number | null {
    for (let value = from; step > 0 ? value <= stop : value >= stop; value += step) {
      if (value >= 0 && value <= last[segment] && this.selectable(segment, value)) return value;
    }
    return null;
  }

  private rovingIndex(segment: Segment): number | null {
    const focus = segment === 'hour' ? this.hourFocus : this.minuteFocus;
    if (focus !== null && this.selectable(segment, focus)) return focus;
    const selected = segment === 'hour' ? this.hour : this.minute;
    if (selected !== null && this.selectable(segment, selected)) return selected;
    return this.find(segment, 0, 1);
  }

  private setFocus(segment: Segment, value: number | null): void {
    if (segment === 'hour') this.hourFocus = value; else this.minuteFocus = value;
  }

  private setSegment(segment: Segment, value: number | null): void {
    const text = value === null ? '' : pad(value);
    if (segment === 'hour') { this.hour = value; this.hourText = text; this.hourInvalid = false; }
    else { this.minute = value; this.minuteText = text; this.minuteInvalid = false; }
  }

  private select(segment: Segment, value: number): void {
    if (!this.selectable(segment, value)) return;
    this.revision++;
    this.setSegment(segment, value);
    this.setFocus(segment, value);
    this.publish();
  }

  private publish(): void {
    const candidate = this.candidate;
    const empty = this.hour === null && this.minute === null;
    if (this.disabled || this.hourInvalid || this.minuteInvalid || (!empty && (candidate === null || !this.inRange(candidate)))) return;
    if (!empty && (!/^\d{1,2}$/.test(this.hourText.trim()) || !/^\d{1,2}$/.test(this.minuteText.trim())
      || Number(this.hourText) !== this.hour || Number(this.minuteText) !== this.minute)) return;
    const next = this.draftValue;
    if (next === this.value) return;
    this.published = this.value = next;
    this.emit('lib-time-picker-change', { value: this.value, revision: this.revision });
    if (!this.hasAttribute('server-state')) this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }

  private commit(segment: Segment): void {
    if (this.disabled) return;
    const text = (segment === 'hour' ? this.hourText : this.minuteText).trim();
    const current = segment === 'hour' ? this.hour : this.minute;
    const invalid = segment === 'hour' ? this.hourInvalid : this.minuteInvalid;
    this.commitText(segment, text, current, invalid);
  }

  private commitText(segment: Segment, text: string, current: number | null, invalid: boolean): void {
    if (text === '') {
      if (current === null && !invalid) return;
      this.setSegment(segment, null);
    } else if (/^\d{1,2}$/.test(text) && Number(text) <= last[segment]) {
      const value = Number(text);
      if (value === current && !invalid && text === pad(value)) return;
      this.setSegment(segment, value);
      this.setFocus(segment, null);
      this.pendingScroll.add(segment);
    } else {
      if (segment === 'hour') this.hourInvalid = true; else this.minuteInvalid = true;
      return;
    }
    this.publish();
  }

  /** Restores the editor to its last accepted segment value. */
  private revert(segment: Segment): void {
    this.revision++;
    const value = segment === 'hour' ? this.hour : this.minute;
    const text = value === null ? '' : pad(value);
    if (segment === 'hour') { this.hourText = text; this.hourInvalid = false; }
    else { this.minuteText = text; this.minuteInvalid = false; }
  }

  private editorInput(event: Event, segment: Segment): void {
    event.stopPropagation();
    const text = (event.target as HTMLInputElement).value;
    if (segment === 'hour') this.hourText = text; else this.minuteText = text;
    if (!this.hourText.trim() && !this.minuteText.trim()) {
      this.setSegment('hour', null);
      this.setSegment('minute', null);
      this.publish();
    }
    this.reportInput();
  }

  private editorKeydown(event: KeyboardEvent, segment: Segment): void {
    if (event.key === 'Enter') this.commit(segment);
    else if (event.key === 'Escape') this.revert(segment);
  }

  private optionKeydown(event: KeyboardEvent, segment: Segment, value: number): void {
    if (this.disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.select(segment, value);
      return;
    }
    const end = last[segment];
    const up = Math.max(0, value - 5), down = Math.min(end, value + 5);
    const targets: Record<string, () => number | null> = {
      ArrowUp: () => this.find(segment, value - 1, -1),
      ArrowDown: () => this.find(segment, value + 1, 1),
      Home: () => this.find(segment, 0, 1),
      End: () => this.find(segment, end, -1),
      PageUp: () => this.find(segment, up, -1) ?? this.find(segment, up + 1, 1, value - 1),
      PageDown: () => this.find(segment, down, 1) ?? this.find(segment, down - 1, -1, value + 1),
    };
    const target = targets[event.key];
    if (!target) return;
    event.preventDefault();
    const next = target();
    if (next === null || next === value) return;
    this.setFocus(segment, next);
    this.pendingFocus = segment;
  }

  private editor(segment: Segment, invalid: boolean, describedBy: string | typeof nothing) {
    const text = segment === 'hour' ? this.hourText : this.minuteText;
    return html`<div class="lib-time-picker-field ${invalid ? 'lib-time-picker-field--error' : ''}">
      <input id="${segment}-input" class="lib-time-picker-input" type="text" inputmode="numeric" maxlength="2"
        autocomplete="off" placeholder="--" .value=${live(text)} ?disabled=${this.disabled}
        aria-label=${segment === 'hour' ? this.hoursLabel : this.minutesLabel}
        aria-invalid=${invalid ? 'true' : nothing} aria-describedby=${describedBy}
        @input=${(event: Event) => this.editorInput(event, segment)}
        @change=${(event: Event) => { event.stopPropagation(); this.commit(segment); }}
        @blur=${() => this.commit(segment)}
        @keydown=${(event: KeyboardEvent) => this.editorKeydown(event, segment)} />
    </div>`;
  }

  private listbox(segment: Segment, invalid: boolean, describedBy: string | typeof nothing) {
    const hour = this.hourInvalid ? null : this.hour;
    const minute = this.minuteInvalid ? null : this.minute;
    const selected = segment === 'hour' ? hour : minute;
    const roving = this.rovingIndex(segment);
    return html`<div id="${segment}-list" class="lib-time-picker-list lib-time-picker-list--${segment}" role="listbox" aria-labelledby="${segment}-label"
      aria-disabled=${this.disabled ? 'true' : nothing} aria-invalid=${invalid ? 'true' : nothing} aria-describedby=${describedBy}>
      ${Array.from({ length: last[segment] + 1 }, (_, value) => {
        const enabled = this.selectable(segment, value);
        const isSelected = value === selected;
        const classes = ['lib-time-picker-option'];
        if (isSelected) classes.push(enabled || this.disabled ? 'lib-time-picker-option--selected' : 'lib-time-picker-option--conflict');
        if (!enabled) classes.push('lib-time-picker-option--disabled');
        const tabIndex = value === roving ? 0 : -1;
        return html`<div id="${segment}-option-${value}" class=${classes.join(' ')} role="option" data-value=${value}
          aria-selected=${String(isSelected)} aria-disabled=${enabled ? nothing : 'true'}
          tabindex=${enabled ? tabIndex : nothing}
          @click=${() => this.select(segment, value)}
          @focus=${() => { if (enabled) this.setFocus(segment, value); }}
          @keydown=${(event: KeyboardEvent) => this.optionKeydown(event, segment, value)}><span class="lib-time-picker-option-text">${pad(value)}</span></div>`;
      })}
    </div>`;
  }

  override render() {
    const feedback = this.feedback;
    const describedBy = feedback ? 'message' : nothing;
    const shared = this.rangeError || this.error || !!feedback?.external;
    let messageClass = '';
    if (feedback) messageClass = `lib-time-picker-message--${feedback.error ? 'error' : 'hint'}`;
    return html`<div class="lib-time-picker ${this.disabled ? 'lib-time-picker--disabled' : ''} ${this.card ? 'lib-time-picker--card' : ''}" role="group" aria-label=${this.groupLabel}>
      <div class="lib-time-picker-editors">
        ${this.editor('hour', this.hourInvalid || shared, describedBy)}
        <span class="lib-time-picker-colon" aria-hidden="true">:</span>
        ${this.editor('minute', this.minuteInvalid || shared, describedBy)}
      </div>
      <div class="lib-time-picker-separator" aria-hidden="true"></div>
      <div class="lib-time-picker-lists">
        <span id="hour-label" class="lib-time-picker-header lib-time-picker-header--hour">${this.hoursLabel}</span>
        <span id="minute-label" class="lib-time-picker-header lib-time-picker-header--minute">${this.minutesLabel}</span>
        ${this.listbox('hour', shared, describedBy)}
        ${this.listbox('minute', shared, describedBy)}
      </div>
      <div id="message" class="lib-time-picker-message ${messageClass}" aria-live="polite">${feedback?.text ?? ''}</div>
    </div>`;
  }
}
declare global { interface HTMLElementTagNameMap { 'lib-time-picker': LibTimePicker; } }
